import React, { ChangeEvent, PureComponent } from 'react';

import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import Fade from '@mui/material/Fade';
import LinearProgress from '@mui/material/LinearProgress';
import { Search as SearchIcon } from '@mui/icons-material';

import {
  Switch,
} from '@mui/material';

import { withTranslation } from 'react-i18next';
import {
  IProps, IState, IStateProps, IDispatchProps,
} from './interfaces';
import {
  Container, QueryControlsRow, InnerQueryControlsRow, SearchInput, SearchButton, SearchResultsContainer, StyledControl, RedSwitch,
} from './styles';
import SearchResults from '../Results/component';
import { UiSection, KeyboardKey } from '../../../enums';
import { ISimpleSearchLocaleLookupKeys, LocaleKeyTree } from '../../../localization';
import { IAppState } from '../../../state/reducers';
import {
  queryGamesAction,
  resetSort,
  resetLimit,
  resetPage,
  updateSearchQueryParams,
  updateSearchQueryString,
  toggleOwnedFilter,
  toggleDateTypeFilter,
  SEARCH_OWNED_FILTER_STORAGE_KEY,
} from '../../../state/search';
import { DatePicker, DatePickerInputStyles, DatePickerButtonProps } from './material-styles';
import { clearAllFilters } from '../../../state/filterAssets';
import { selectIsUserExchangeAdmin } from '../../../state/user/userOrgRole';
import {
  setEndOfDay, setStartOfDay,
} from '../../../utils/time';

const From = 'from';
const To = 'to';

class SimpleSearch extends PureComponent<IProps, IState> {
  private static readonly searchInputID = `${UiSection.SimpleSearch}.Input`;
  private static readonly searchButtonID = `${UiSection.SimpleSearch}.Submit`;
  private static readonly searchDateFromID = `${UiSection.SimpleSearch}.DateFromInput`;
  private static readonly searchDateToID = `${UiSection.SimpleSearch}.DateToInput`;

  private readonly localeKeys: Readonly<ISimpleSearchLocaleLookupKeys> = LocaleKeyTree.simpleSearch;

  componentDidMount() {
    const {
      loggedIn,
      isExchangeAdmin,
      toggleOwned,
    } = this.props;
    // note: should not be mounted without being logged in because of va-1195
    // if statement just a safe guard to not disrupt application

    document.title = 'Vault';

    if (loggedIn) {
      if (isExchangeAdmin && localStorage.getItem(SEARCH_OWNED_FILTER_STORAGE_KEY) === null) {
        toggleOwned();
      }
      // delaying 500ms for nextTick state update
      // ensure search query picks up owner param if owned toggled
      this.triggerQuery(500, true);
    }
  }

  handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { updateSearchQueryStringConnect } = this.props;
    const { value } = e.target;

    const queryString = value;

    updateSearchQueryStringConnect(queryString);
  };

  handleInputKeyPress = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === KeyboardKey.Enter) {
      this.triggerQuery();
    }
  };

  triggerQuery = (timeout: number = 0, firstQuery: boolean = false) => {
    const {
      queryGamesActionConnect,
      isQuerying,
      resetSortConnect,
      resetLimitConnect,
      resetPageConnect,
      clearAllFiltersConnect,
      updateSearchQueryParamsConnect,
      queryString,
    } = this.props;

    updateSearchQueryParamsConnect({
      queryString,
    });

    if (!isQuerying) {
      clearAllFiltersConnect();
      resetLimitConnect();
      resetSortConnect();
      resetPageConnect();
      queryGamesActionConnect(timeout, null, firstQuery);
    }
  };

  onChangeHandler = (value: Date, which: string) => {
    const { updateSearchQueryParamsConnect } = this.props;
    switch (which) {
      case To:
        updateSearchQueryParamsConnect({
          toDate: value,
        });
        break;
      case From:
        updateSearchQueryParamsConnect({
          fromDate: value,
        });
        break;
      default:
        break;
    }
  };

  renderDatePicker(which: string) {
    const { isQuerying, queryParams: { fromDate, toDate } } = this.props;
    const id = (which === From) ? SimpleSearch.searchDateFromID : SimpleSearch.searchDateToID;
    const dataAuto = (which === From) ? 'SimpleFromDateInput' : 'SimpleToDateInput';
    const value = (which === From) ? setStartOfDay(fromDate) : setEndOfDay(toDate);
    return (
      <DatePicker
        // id={id}
        data-auto={dataAuto}
        className={dataAuto}
        disabled={isQuerying}
        // variant="inline"
        format="MM-dd-yyyy"
        value={value}
        // invalidDateMessage=""
        disableFuture
        // disableToolbar
        // InputAdornmentProps={{ position: 'end' }}
        // KeyboardButtonProps={DatePickerButtonProps}
        // InputProps={DatePickerInputStyles}
        onChange={(e: any) => this.onChangeHandler((which === From) ? setStartOfDay(e as Date | null) : setEndOfDay(e as Date | null), which)}
        slotProps={{
          actionBar: {
            actions: ['clear', 'today'],
          },
        }}
      />
    );
  }

  renderDateTypeSwitch(which: string) {
    const { isCreatedDateTypeSearch, queryParams: { fromDate, toDate } } = this.props;
    return (
      <StyledControl
        label={isCreatedDateTypeSearch ? 'Created Date' : 'Game Date'}
        data-auto="DateTypeSwitch"
        labelPlacement="end"
        control={(
          <RedSwitch
            color="secondary"
            checked={isCreatedDateTypeSearch}
            onChange={(e: any) => {
              this.handleDateTypeToggle(e, isCreatedDateTypeSearch);
              if (fromDate || toDate) {
                this.triggerQuery();
              }
            }}
          />
        )}
      />
    );
  }

  handleDateTypeToggle = (value: Date, which: boolean) => {
    const { updateSearchQueryParamsConnect } = this.props;
    const {
      toggleDateType,
    } = this.props;

    toggleDateType();// toggle local storage
    updateSearchQueryParamsConnect({
      isCreatedDateTypeSearch: which,
    });
  };

  render() {
    const {
      t, isQuerying, queryString,
    } = this.props;

    return (
      <Fade in>
        <Container>
          <QueryControlsRow>
            <InnerQueryControlsRow>
              <SearchInput
                $isQuerying={isQuerying}
                value={queryString}
                id={SimpleSearch.searchInputID}
                data-auto="SimpleSearchInput"
                disabled={isQuerying}
                placeholder={t(this.localeKeys.searchInputPlaceholder)}
                onChange={this.handleOnChange}
                onKeyPress={this.handleInputKeyPress}
              />

              {this.renderDateTypeSwitch(From)}
              {this.renderDatePicker(From)}
              {this.renderDatePicker(To)}

              <SearchButton
                id={SimpleSearch.searchButtonID}
                data-auto="SimpleSearchBtn"
                variant="primaryOnBlack"
                disabled={isQuerying}
                onClick={(e) => this.triggerQuery()}
              >
                {t(this.localeKeys.searchButtonText)}

                <SearchIcon />
              </SearchButton>

            </InnerQueryControlsRow>
          </QueryControlsRow>

          <SearchResultsContainer id="Search.Result.Container">
            {isQuerying && <LinearProgress />}
            {React.createElement(SearchResults)}
          </SearchResultsContainer>

        </Container>
      </Fade>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  isQuerying: state.search.isQuerying,
  isCreatedDateTypeSearch: state.search?.isCreatedDateTypeSearch ?? false,
  queryParams: state.search?.queryParams,
  queryString: state.search?.queryString ?? '',
  securityProfiles: state.securityProfiles?.idValueMapping ?? [],
  loggedIn: state.user?.userOrgRole?.loggedIn ?? false,
  isExchangeAdmin: selectIsUserExchangeAdmin(state),
});

// Note, giving the action an alias is to avoid lint's "no-shadow" error
const mapDispatchToProps: IDispatchProps = {
  queryGamesActionConnect: queryGamesAction,
  updateSearchQueryParamsConnect: updateSearchQueryParams,
  updateSearchQueryStringConnect: updateSearchQueryString,
  resetSortConnect: resetSort,
  resetLimitConnect: resetLimit,
  resetPageConnect: resetPage,
  clearAllFiltersConnect: clearAllFilters,
  toggleOwned: toggleOwnedFilter,
  toggleDateType: toggleDateTypeFilter,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslation()(SimpleSearch));
